/**
 * BBC PRODUCT CATALOG
 * ------------------------------------------------------------
 * Source of truth for the entire product taxonomy.
 * Any UI (landing portfolio, /products/:family, /products/:family/:sku,
 * quote forms, insights cross-links) reads from this file.
 *
 * Edit here → everything updates.
 *
 * Structure:
 *   FAMILIES: array of { slug, name, wattage, tagline, positioning,
 *                        useCases, heroImage, accentColor, skus[] }
 *   SKU: { code, name, tagline, image, specs{}, cabinet, highlights[] }
 */

export const FAMILIES = [
  {
    slug: "prime",
    name: "PRIME",
    wattage: "120W",
    tagline: "Reliable. Everyday. Residential-first.",
    positioning:
      "The everyday workhorse of the BBC lineup. Pre-tuned, silent, and built for the entries that move most of India's traffic — societies, villas, small commercial gates.",
    useCases: [
      "Residential societies & apartment complexes",
      "Villa and gated community entries",
      "Small commercial buildings",
      "Routine-access lanes (low to medium volume)",
    ],
    heroImage: "/branded/bbc-full-barrier-orange.png",
    accentColor: "#A1A1AA",
    motorClass: "BLDC 120W · 160:1 gear",
    speedRange: "2.5 – 6 sec",
    armRange: "3 – 6 m telescopic",
    skus: [
      {
        code: "BP-01",
        name: "PRIME BP-01",
        tagline: "Field-proven classic",
        image: "/branded/bbc-cab-classic-green.png",
        cabinet: "MS Powder-Coated · 1.2mm · 18–20 kg",
        highlights: [
          "OEM classic cabinet",
          "BLDC 120W silent drive",
          "IP65 all-weather",
        ],
        specs: {
          Motor: "BLDC 120W",
          Gear: "160:1 worm reduction",
          "Cycle Time": "2.5–6 sec (arm-dependent)",
          "Boom Length": "3–6 m telescopic",
          Body: "1.2mm MS powder-coated",
          "Body Weight": "18–20 kg",
          "IP Rating": "IP65",
          Warranty: "24 months mechanical",
        },
      },
      {
        code: "BP-02",
        name: "PRIME BP-02",
        tagline: "LED signature",
        image: "/branded/bbc-cab-led-branded-green.png",
        cabinet: "GI Powder-Coated · 1.5mm · 20 kg · Top LED integrated",
        highlights: [
          "Integrated top LED signature",
          "GI galvanised body — rust-resistant",
          "Premium field visibility",
        ],
        specs: {
          Motor: "BLDC 120W",
          Gear: "160:1 worm reduction",
          "Cycle Time": "2.5–6 sec",
          "Boom Length": "3–6 m telescopic",
          Body: "1.5mm GI powder-coated",
          "Body Weight": "20 kg",
          "IP Rating": "IP65",
          Lighting: "Integrated top LED",
          Warranty: "24 months mechanical",
        },
      },
      {
        code: "BP-03",
        name: "PRIME BP-03",
        tagline: "Slim metallic premium",
        image: "/branded/bbc-cab-slim-champagne.png",
        cabinet: "MS Powder-Coated · 1.2mm · 18–20 kg · Slim form",
        highlights: [
          "Premium slim design",
          "Softer visual footprint",
          "Suited for boutique entries",
        ],
        specs: {
          Motor: "BLDC 120W",
          Gear: "160:1 worm reduction",
          "Cycle Time": "2.5–6 sec",
          "Boom Length": "3–6 m telescopic",
          Body: "1.2mm MS powder-coated · slim form",
          "Body Weight": "18–20 kg",
          "IP Rating": "IP65",
          Warranty: "24 months mechanical",
        },
      },
      {
        code: "BP-04",
        name: "PRIME BP-04",
        tagline: "Glass-front light panel",
        image: "/branded/bbc-cab-glass-p-status.png",
        cabinet: "MS Powder-Coated · 1.2mm · Glass-front light panel",
        highlights: [
          "Glass-front illuminated panel",
          "Status graphic capable",
          "Statement architectural finish",
        ],
        specs: {
          Motor: "BLDC 120W",
          Gear: "160:1 worm reduction",
          "Cycle Time": "2.5–6 sec",
          "Boom Length": "3–6 m telescopic",
          Body: "1.2mm MS · glass-front insert",
          "Body Weight": "18–20 kg",
          "IP Rating": "IP65",
          Lighting: "Front glass light panel",
          Warranty: "24 months mechanical",
        },
      },
    ],
  },
  {
    slug: "elite",
    name: "ELITE",
    wattage: "150W",
    tagline: "Higher load. Higher duty. Commercial-grade.",
    positioning:
      "Built for commercial and industrial entries where load, arm length and duty cycle matter — corporate campuses, factories, logistics gates, mid-tier toll lanes.",
    useCases: [
      "Corporate campuses & office parks",
      "Factory and warehouse gates",
      "Logistics and last-mile hubs",
      "Mid-frequency access with longer arms",
    ],
    heroImage: "/branded/bbc-full-barrier-orange.png",
    accentColor: "#B0FFD9",
    motorClass: "BLDC 150W · 160:1 gear",
    speedRange: "2.5 – 6 sec",
    armRange: "3 – 6 m telescopic / fence arm",
    skus: [
      {
        code: "BE-01",
        name: "ELITE BE-01",
        tagline: "LED signature workhorse",
        image: "/branded/bbc-cab-led-branded-green.png",
        cabinet: "GI Powder-Coated · 1.5mm · 20 kg · Top LED integrated",
        highlights: [
          "GI galvanised body",
          "Integrated top LED",
          "Higher-load 150W drive",
        ],
        specs: {
          Motor: "BLDC 150W",
          Gear: "160:1 worm reduction",
          "Cycle Time": "2.5–6 sec",
          "Boom Length": "3–6 m telescopic / fence arm",
          Body: "1.5mm GI powder-coated",
          "Body Weight": "20 kg",
          "IP Rating": "IP65",
          Lighting: "Integrated top LED",
          Warranty: "24 months mechanical",
        },
      },
      {
        code: "BE-02",
        name: "ELITE BE-02",
        tagline: "Top + gate LED",
        image: "/branded/bbc-cab-led-status-white.png",
        cabinet: "GI Powder-Coated · 1.5mm · 20 kg · Top + gate LED",
        highlights: [
          "Dual-LED (top + gate arm)",
          "Enhanced night visibility",
          "Corporate-grade finish",
        ],
        specs: {
          Motor: "BLDC 150W",
          Gear: "160:1 worm reduction",
          "Cycle Time": "2.5–6 sec",
          "Boom Length": "3–6 m telescopic / fence arm",
          Body: "1.5mm GI powder-coated",
          "Body Weight": "20 kg",
          "IP Rating": "IP65",
          Lighting: "Top + gate LED",
          Warranty: "24 months mechanical",
        },
      },
      {
        code: "BE-03",
        name: "ELITE BE-03",
        tagline: "Premium glass finish",
        image: "/branded/bbc-cab-glass-p-status.png",
        cabinet: "MS Powder-Coated · 1.2mm · Premium glass finish",
        highlights: [
          "Architectural glass front",
          "Boutique commercial appeal",
          "150W higher-load drive",
        ],
        specs: {
          Motor: "BLDC 150W",
          Gear: "160:1 worm reduction",
          "Cycle Time": "2.5–6 sec",
          "Boom Length": "3–6 m telescopic / fence arm",
          Body: "1.2mm MS · glass finish",
          "Body Weight": "18–20 kg",
          "IP Rating": "IP65",
          Warranty: "24 months mechanical",
        },
      },
      {
        code: "BE-04",
        name: "ELITE BE-04",
        tagline: "Advertising / video display",
        image: "/branded/bbc-cab-video-display.png",
        cabinet: "GI Powder-Coated · 1.5mm · 30 kg · Image + video display",
        highlights: [
          "Image & video playback front panel",
          "Offline + online CMS ready",
          "Advertising / branding SKU",
        ],
        specs: {
          Motor: "BLDC 150W",
          Gear: "160:1 worm reduction",
          "Cycle Time": "2.5–6 sec",
          "Boom Length": "3–6 m telescopic / fence arm",
          Body: "1.5mm GI powder-coated · display cabinet",
          "Body Weight": "30 kg",
          "IP Rating": "IP65",
          Display: "Image + video, offline + online CMS",
          Warranty: "24 months mechanical",
        },
      },
    ],
  },
  {
    slug: "flash",
    name: "FLASH",
    wattage: "180W",
    tagline: "Sub-second cycle. Throughput-first.",
    positioning:
      "The high-speed lane specialist. Sub-second cycle times built for parking exits, corporate parks, toll lanes and anywhere throughput is the constraint.",
    useCases: [
      "Toll lanes & FASTag booths",
      "Corporate park exits",
      "Premium parking & lane exits",
      "High-frequency shift changes",
    ],
    heroImage: "/branded/bbc-full-barrier-orange.png",
    accentColor: "#00FF85",
    motorClass: "BLDC 180W · 40:1 gear (high-speed)",
    speedRange: "0.6 – 1.5 sec",
    armRange: "3 m round arm",
    skus: [
      {
        code: "BF-01",
        name: "FLASH BF-01",
        tagline: "LED signature speed",
        image: "/branded/bbc-cab-led-branded-green.png",
        cabinet: "GI Powder-Coated · 1.5mm · 20 kg · Top LED integrated",
        highlights: [
          "0.6–1.5s cycle time",
          "Integrated top LED",
          "GI corrosion-resistant body",
        ],
        specs: {
          Motor: "BLDC 180W",
          Gear: "40:1 high-speed reduction",
          "Cycle Time": "0.6–1.5 sec",
          "Boom Length": "3 m round arm",
          Body: "1.5mm GI powder-coated",
          "Body Weight": "20 kg",
          "IP Rating": "IP65",
          Lighting: "Integrated top LED",
          Warranty: "24 months mechanical",
        },
      },
      {
        code: "BF-02",
        name: "FLASH BF-02",
        tagline: "Top + gate LED",
        image: "/branded/bbc-cab-led-status-white.png",
        cabinet: "GI Powder-Coated · 1.5mm · 20 kg · Top + gate LED",
        highlights: [
          "Dual-LED for lane visibility",
          "Sub-second cycle",
          "Toll-lane ready",
        ],
        specs: {
          Motor: "BLDC 180W",
          Gear: "40:1 high-speed reduction",
          "Cycle Time": "0.6–1.5 sec",
          "Boom Length": "3 m round arm",
          Body: "1.5mm GI powder-coated",
          "Body Weight": "20 kg",
          "IP Rating": "IP65",
          Lighting: "Top + gate LED",
          Warranty: "24 months mechanical",
        },
      },
      {
        code: "BF-03",
        name: "FLASH BF-03",
        tagline: "Premium glass finish",
        image: "/branded/bbc-cab-glass-p-status.png",
        cabinet: "MS Powder-Coated · 1.2mm · Premium glass finish",
        highlights: [
          "Architectural glass front",
          "Corporate lobby-grade",
          "High-speed drive",
        ],
        specs: {
          Motor: "BLDC 180W",
          Gear: "40:1 high-speed reduction",
          "Cycle Time": "0.6–1.5 sec",
          "Boom Length": "3 m round arm",
          Body: "1.2mm MS · glass finish",
          "Body Weight": "18–20 kg",
          "IP Rating": "IP65",
          Warranty: "24 months mechanical",
        },
      },
      {
        code: "BF-04",
        name: "FLASH BF-04",
        tagline: "Advertising / video display",
        image: "/branded/bbc-cab-video-display.png",
        cabinet: "GI Powder-Coated · 1.5mm · 30 kg · Image + video display",
        highlights: [
          "Image & video playback",
          "Offline + online CMS",
          "Fast lane + brand messaging",
        ],
        specs: {
          Motor: "BLDC 180W",
          Gear: "40:1 high-speed reduction",
          "Cycle Time": "0.6–1.5 sec",
          "Boom Length": "3 m round arm",
          Body: "1.5mm GI powder-coated · display cabinet",
          "Body Weight": "30 kg",
          "IP Rating": "IP65",
          Display: "Image + video, offline + online CMS",
          Warranty: "24 months mechanical",
        },
      },
    ],
  },
  {
    slug: "max",
    name: "MAX",
    wattage: "220W",
    tagline: "Long-span. Heavy-duty. Wide gates.",
    positioning:
      "The wide-gate specialist. Twin-spindle heavy-duty assembly and reinforced truss booms clear spans up to 12 meters — engineered for industrial gates, toll plazas and airside applications.",
    useCases: [
      "Toll plazas (wide span)",
      "Industrial and factory gates",
      "Ports, logistics yards, airside",
      "Any lane 8m – 12m wide",
    ],
    heroImage: "/branded/bbc-max-factory.jpeg",
    accentColor: "#FFB84D",
    motorClass: "BLDC 220W · twin-spindle heavy-duty",
    speedRange: "5 – 8 sec adjustable",
    armRange: "8 m · 11–12 m truss",
    skus: [
      {
        code: "BM-01",
        name: "MAX BM-01",
        tagline: "8m / 11–12m truss barrier",
        image: "/branded/bbc-max-orange-full.png",
        gallery: [
          "/branded/bbc-max-orange-full.png",
          "/branded/bbc-max-factory.jpeg",
        ],
        cabinet: "Heavy-duty industrial cabinet · reinforced base",
        highlights: [
          "8m heavy-duty or 11–12m extra-long",
          "Twin-spindle assembly",
          "Reinforced truss boom",
          "Double-beam photocell included",
        ],
        specs: {
          Motor: "BLDC 220W",
          Assembly: "Twin-spindle heavy-duty",
          "Cycle Time": "5–8 sec adjustable",
          "Boom Length": "8 m / 11–12 m reinforced truss",
          Body: "Heavy-duty GI · reinforced base",
          "IP Rating": "IP65",
          Safety: "Double-beam photocell included",
          Warranty: "24 months mechanical",
        },
      },
    ],
  },
];

/* --------------------------- lookups --------------------------- */

export const getFamily = (slug) =>
  FAMILIES.find((f) => f.slug === slug?.toLowerCase());

export const getSku = (familySlug, skuCode) => {
  const family = getFamily(familySlug);
  if (!family) return null;
  const sku = family.skus.find(
    (s) => s.code.toLowerCase() === skuCode?.toLowerCase(),
  );
  return sku ? { family, sku } : null;
};

export const ALL_SKUS = FAMILIES.flatMap((f) =>
  f.skus.map((s) => ({ family: f, sku: s })),
);

/* --------------------------- shared ecosystem --------------------------- */

export const ECOSYSTEM = {
  smartPlatform: [
    "Password-protected controller access",
    "AMC cycle-limit & day-limit control",
    "Auto lock/unlock on cycle exhaustion",
    "BBC Connect ready — remote monitor & control",
    "BBC Trust — tested, tuned, video-recorded before dispatch",
  ],
  connect: [
    "Live status monitoring",
    "Remote open / close / stop",
    "Alerts & fault diagnostics",
    "Reports & access logs",
    "Multi-site fleet dashboard",
  ],
  rs485: [
    "PMS / access-control integration",
    "Real-time status",
    "Remote open/close/stop",
    "Access logs & usage",
    "Fault alerts & diagnostics",
  ],
  safety: [
    "Single-beam photocell",
    "Dual-beam photocell",
    "PD132 loop detector",
    "RFID reader (6m / 15m / 20m)",
  ],
  warranty: [
    "24 months BBC mechanical warranty",
    "30 months Delta SMPS brand guarantee",
    "12 months Polycab MCB brand guarantee",
  ],
};

export const CABINET_STYLES = [
  { name: "Classic / OEM", desc: "Simple field-proven cabinet." },
  { name: "LED Signature", desc: "Top LED + premium front insert." },
  { name: "Slim Metallic", desc: "Premium slim form factor." },
  { name: "Glass Front", desc: "Illuminated status graphic front." },
  { name: "Advertising", desc: "Image & video capable display cabinet." },
];
